// KbTrainingDetail.jsx — the "How my calls are scored" reading page
// for a single KB. Renders every criterion with its weight, the % of
// total each contributes, and a per-criterion progress-style bar so
// the visual hierarchy of what matters most is obvious at a glance.
//
// On mount, records a "viewed" event; the "Acknowledge" button records
// the ack against the KB's current content_hash — later changes flip
// the module back into recert-required.
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, RotateCcw, ArrowLeft, Target, Award, HelpCircle, XCircle, ListOrdered, MessageSquare, Quote, ShieldAlert, AlertTriangle, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

// ── Inline quiz component ────────────────────────────────────────────────────
// Optional lightweight quiz that lets a user "earn" the acknowledgement
// by demonstrating they understand the KB criteria instead of just
// clicking "I understand". Passing (≥80% by default) auto-ACKs the
// module — same DB row as the plain acknowledge button — so recert
// logic works identically.
const InlineQuiz = ({ kbId, token, onPassed }) => {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!kbId) return;
    setQuiz(null); setResult(null); setAnswers({});
    axios.get(`${API}/kb-training/kbs/${kbId}/quiz`, { headers: authHeaders(token) })
      .then((r) => setQuiz(r.data))
      .catch(() => setQuiz({ error: true }));
  }, [kbId, token]);

  const handleSubmit = async () => {
    if (!quiz?.questions) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/kb-training/kbs/${kbId}/quiz/submit`,
        { answers },
        { headers: authHeaders(token) },
      );
      setResult(r.data);
      if (r.data.passed) {
        toast.success(`Passed with ${r.data.score}% — module acknowledged.`);
        onPassed?.();
      } else {
        toast.error(`Scored ${r.data.score}% — need ${r.data.pass_score}% to pass. Review and retry.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit quiz. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!quiz) return <div className="text-xs text-[color:var(--tf-muted)] italic">Loading quiz…</div>;
  if (quiz.error) return <div className="text-xs text-rose-600">Failed to load quiz.</div>;
  if (!quiz.questions?.length) return <div className="text-xs text-[color:var(--tf-muted)] italic">No quiz available for this KB.</div>;

  const allAnswered = quiz.questions.every((q) => !!answers[q.id]);

  return (
    <div className="space-y-4" data-testid="kb-quiz">
      {quiz.questions.map((q, qi) => {
        const chosen = answers[q.id];
        const qResult = (result?.per_q || []).find((r) => r.q_id === q.id);
        return (
          <div key={q.id} className="border border-[color:var(--tf-forest)]/10 rounded-lg p-3" data-testid={`kb-quiz-q-${q.id}`}>
            <p className="text-sm font-semibold text-[color:var(--tf-forest)] mb-2">
              {qi + 1}. {q.prompt}
            </p>
            <div className="space-y-1.5">
              {q.choices.map((c) => {
                const isChosen = chosen === c.id;
                const showResult = !!qResult;
                const isCorrectAnswer = showResult && qResult.correct === c.id;
                const isWrongChoice = showResult && isChosen && !qResult.is_right;
                const tone = showResult
                  ? isCorrectAnswer ? 'border-emerald-400 bg-emerald-500/10'
                    : isWrongChoice ? 'border-rose-400 bg-rose-500/10'
                      : 'border-[color:var(--tf-forest)]/10'
                  : isChosen ? 'border-[color:var(--tf-gold-2)] bg-amber-500/5' : 'border-[color:var(--tf-forest)]/10 hover:bg-[color:var(--tf-forest)]/5';
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => !result && setAnswers((a) => ({ ...a, [q.id]: c.id }))}
                    disabled={!!result}
                    className={`w-full text-left rounded border p-2 text-xs transition-colors ${tone} ${result ? 'cursor-default' : 'cursor-pointer'}`}
                    data-testid={`kb-quiz-choice-${q.id}-${c.id}`}
                  >
                    <span className="text-[color:var(--tf-forest)]">{c.text}</span>
                    {showResult && isCorrectAnswer && (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 inline ml-2" />
                    )}
                    {showResult && isWrongChoice && (
                      <XCircle className="w-3.5 h-3.5 text-rose-600 inline ml-2" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      {!result ? (
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!allAnswered || busy}
          className="tf-btn-primary px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
          data-testid="kb-quiz-submit-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Award className="w-3.5 h-3.5" />}
          Submit quiz
        </button>
      ) : (
        <div
          className="rounded-lg p-3 text-sm"
          style={{
            background: result.passed ? 'rgba(16,185,129,0.10)' : 'rgba(244,63,94,0.10)',
            color: result.passed ? '#065f46' : '#9f1239',
          }}
          data-testid="kb-quiz-result"
        >
          <p className="font-semibold">
            {result.passed ? `Passed with ${result.score}%` : `Scored ${result.score}% — need ${result.pass_score}%`}
          </p>
          <p className="text-xs mt-0.5">{result.correct} of {result.total} correct</p>
        </div>
      )}
    </div>
  );
};


const KbTrainingDetail = ({ token }) => {
  const { kbId } = useParams();
  const [data, setData] = useState(null);
  const [ackBusy, setAckBusy] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);

  useEffect(() => {
    if (!kbId) return;
    setData(null);
    axios
      .get(`${API}/kb-training/kbs/${kbId}`, { headers: authHeaders(token) })
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [kbId, token]);

  const handleAck = async () => {
    setAckBusy(true);
    try {
      await axios.post(`${API}/kb-training/kbs/${kbId}/ack`, {}, { headers: authHeaders(token) });
      const r = await axios.get(`${API}/kb-training/kbs/${kbId}`, { headers: authHeaders(token) });
      setData(r.data);
      toast.success('Acknowledged — thanks!');
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Could not save your acknowledgement. Please try again.');
    } finally {
      setAckBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="tf-card rounded-xl p-8 text-center" data-testid="kb-training-detail-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-[color:var(--tf-forest)]" />
      </div>
    );
  }
  if (data.error) {
    return (
      <div className="tf-card rounded-xl p-6 text-center">
        <p className="text-sm text-rose-600 font-semibold">Failed to load knowledge base</p>
        <Link
          to="/training"
          className="text-xs text-[color:var(--tf-forest)] underline mt-2 inline-block"
        >
          Back to training
        </Link>
      </div>
    );
  }

  const acked = !!data.progress?.acked_at;
  const recert = !!data.progress?.recert_required;

  return (
    <div className="space-y-5" data-testid="kb-training-detail">
      <Link
        to="/training"
        className="text-xs text-[color:var(--tf-forest)]/70 hover:text-[color:var(--tf-forest)] inline-flex items-center gap-1"
      >
        <ArrowLeft className="w-3 h-3" /> Back to training
      </Link>

      <div className="tf-card rounded-xl p-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="flex-1 min-w-0">
            <p className="tf-eyebrow">{(data.call_type || '').replace(/_/g, ' ')} · scoring guide</p>
            <h2 className="text-xl font-bold text-[color:var(--tf-forest)] mt-0.5">{data.title}</h2>
            {data.description && (
              <p className="text-sm text-[color:var(--tf-muted)] mt-1">{data.description}</p>
            )}
          </div>
          {recert && (
            <span
              className="text-[10px] uppercase font-bold px-2 py-1 rounded shrink-0"
              style={{ background: 'rgba(200,151,74,0.18)', color: 'var(--tf-gold-2)' }}
              data-testid="kb-training-detail-recert-badge"
            >
              <RotateCcw className="w-3 h-3 inline mr-0.5" /> Recert required
            </span>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4 text-center">
          <div className="rounded-lg p-3" style={{ background: 'rgba(45,90,63,0.06)' }}>
            <p className="text-[10px] uppercase text-[color:var(--tf-muted)] font-semibold">Criteria</p>
            <p className="text-lg font-bold text-[color:var(--tf-forest)] tabular-nums">{data.criteria.length}</p>
          </div>
          <div className="rounded-lg p-3" style={{ background: 'rgba(45,90,63,0.06)' }}>
            <p className="text-[10px] uppercase text-[color:var(--tf-muted)] font-semibold">Max score</p>
            <p className="text-lg font-bold text-[color:var(--tf-forest)] tabular-nums">{data.total_weight}</p>
          </div>
          <div className="rounded-lg p-3" style={{ background: 'rgba(200,151,74,0.10)' }}>
            <p className="text-[10px] uppercase font-semibold" style={{ color: 'var(--tf-gold-2)' }}>Pass threshold</p>
            <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--tf-gold-2)' }}>{data.threshold_score}%</p>
          </div>
        </div>
      </div>

      {/* Criteria list */}
      <div className="tf-card rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-[color:var(--tf-forest)]" />
          <h3 className="font-bold text-[color:var(--tf-forest)]">What we grade on</h3>
        </div>
        <div className="space-y-3">
          {data.criteria.map((c, i) => (
            <div key={i} className="border-l-2 pl-3 py-1" style={{ borderColor: 'var(--tf-gold-2)' }} data-testid={`kb-criterion-${i}`}>
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <p className="text-sm text-[color:var(--tf-forest)] font-medium leading-snug">
                  {c.description}
                </p>
                <span className="text-xs font-bold tabular-nums text-[color:var(--tf-forest)] shrink-0">
                  {c.weight} pts <span className="text-[color:var(--tf-muted)] font-normal">· {c.pct_of_total}%</span>
                </span>
              </div>
              <div className="h-1 rounded-full bg-[color:var(--tf-forest)]/10 overflow-hidden">
                <div
                  className="h-full"
                  style={{ width: `${c.pct_of_total}%`, background: 'var(--tf-gold-2)' }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Subtypes (if any) */}
      {data.subtypes?.length > 0 && (
        <div className="tf-card rounded-xl p-5" data-testid="kb-subtypes-section">
          <h3 className="font-bold text-[color:var(--tf-forest)] mb-2">Call subtypes</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {data.subtypes.map((s, i) => (
              <div key={i} className="rounded-md p-2 text-xs" style={{ background: 'rgba(45,90,63,0.05)' }}>
                <p className="font-semibold text-[color:var(--tf-forest)]">{s.name}</p>
                {s.description && <p className="text-[color:var(--tf-muted)] mt-0.5">{s.description}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audience + Purpose + Core standard (Yabbit KB header block) */}
      {(data.audience || data.purpose || data.core_standard) && (
        <div className="tf-card rounded-xl p-5" data-testid="kb-context-section">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-[color:var(--tf-forest)]" />
            <h3 className="font-bold text-[color:var(--tf-forest)]">Context</h3>
          </div>
          <dl className="space-y-2 text-sm">
            {data.audience && (
              <div>
                <dt className="tf-eyebrow">Audience</dt>
                <dd className="text-[color:var(--tf-forest)]">{data.audience}</dd>
              </div>
            )}
            {data.purpose && (
              <div>
                <dt className="tf-eyebrow">Purpose</dt>
                <dd className="text-[color:var(--tf-forest)]">{data.purpose}</dd>
              </div>
            )}
            {data.core_standard && (
              <div>
                <dt className="tf-eyebrow">Core standard</dt>
                <dd className="text-[color:var(--tf-forest)] italic">{data.core_standard}</dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {/* Ordered flow */}
      {data.ordered_flow?.length > 0 && (
        <div className="tf-card rounded-xl p-5" data-testid="kb-ordered-flow-section">
          <div className="flex items-center gap-2 mb-3">
            <ListOrdered className="w-4 h-4 text-[color:var(--tf-forest)]" />
            <h3 className="font-bold text-[color:var(--tf-forest)]">Ordered flow</h3>
          </div>
          <ol className="space-y-2.5">
            {data.ordered_flow.map((step, i) => {
              const [title, body] = Array.isArray(step) ? step : [step, ''];
              return (
                <li key={i} className="border-l-2 pl-3 py-1" style={{ borderColor: 'var(--tf-gold-2)' }} data-testid={`kb-flow-step-${i}`}>
                  <p className="text-sm font-semibold text-[color:var(--tf-forest)] leading-snug">{title}</p>
                  {body && <p className="text-xs text-[color:var(--tf-muted)] mt-0.5 leading-relaxed">{body}</p>}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Diagnostic / discovery questions */}
      {data.questions?.length > 0 && (
        <div className="tf-card rounded-xl p-5" data-testid="kb-questions-section">
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle className="w-4 h-4 text-[color:var(--tf-forest)]" />
            <h3 className="font-bold text-[color:var(--tf-forest)]">Diagnostic questions</h3>
          </div>
          <ul className="space-y-1.5 list-disc pl-5">
            {data.questions.map((q, i) => (
              <li key={i} className="text-sm text-[color:var(--tf-forest)] leading-snug" data-testid={`kb-question-${i}`}>{q}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Approved phrases */}
      {data.phrases?.length > 0 && (
        <div className="tf-card rounded-xl p-5" data-testid="kb-phrases-section">
          <div className="flex items-center gap-2 mb-3">
            <Quote className="w-4 h-4 text-[color:var(--tf-forest)]" />
            <h3 className="font-bold text-[color:var(--tf-forest)]">Approved phrases</h3>
          </div>
          <div className="space-y-2">
            {data.phrases.map((p, i) => (
              <blockquote
                key={i}
                className="border-l-2 pl-3 py-1 text-sm text-[color:var(--tf-forest)] italic leading-snug"
                style={{ borderColor: 'var(--tf-forest)', background: 'rgba(45,90,63,0.04)' }}
                data-testid={`kb-phrase-${i}`}
              >
                {p}
              </blockquote>
            ))}
          </div>
        </div>
      )}

      {/* Objections + handling */}
      {data.objections?.length > 0 && (
        <div className="tf-card rounded-xl p-5" data-testid="kb-objections-section">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-[color:var(--tf-forest)]" />
            <h3 className="font-bold text-[color:var(--tf-forest)]">Objections &amp; how we handle them</h3>
          </div>
          <div className="space-y-2.5">
            {data.objections.map((o, i) => {
              const [title, guidance] = Array.isArray(o) ? o : [o, ''];
              return (
                <div key={i} className="rounded-md p-3" style={{ background: 'rgba(200,151,74,0.08)' }} data-testid={`kb-objection-${i}`}>
                  <p className="text-sm font-semibold text-[color:var(--tf-forest)]">{title}</p>
                  {guidance && <p className="text-xs text-[color:var(--tf-forest)]/80 mt-1 leading-relaxed">{guidance}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Avoid */}
      {data.avoid?.length > 0 && (
        <div className="tf-card rounded-xl p-5" data-testid="kb-avoid-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4" style={{ color: '#b45309' }} />
            <h3 className="font-bold text-[color:var(--tf-forest)]">Avoid</h3>
          </div>
          <ul className="space-y-1.5 list-disc pl-5">
            {data.avoid.map((a, i) => (
              <li key={i} className="text-sm text-[color:var(--tf-forest)] leading-snug" data-testid={`kb-avoid-${i}`}>{a}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Governance — never green even at high score, per Phase 5 spec */}
      {data.governance?.length > 0 && (
        <div
          className="tf-card rounded-xl p-5"
          style={{ borderTop: '3px solid #b91c1c' }}
          data-testid="kb-governance-section"
        >
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="w-4 h-4" style={{ color: '#b91c1c' }} />
            <h3 className="font-bold" style={{ color: '#b91c1c' }}>Governance — non-negotiable</h3>
          </div>
          <p className="text-xs text-[color:var(--tf-muted)] mb-3 italic">
            Any breach is flagged for the coach independently of the numeric score.
          </p>
          <ul className="space-y-1.5 list-disc pl-5">
            {data.governance.map((g, i) => (
              <li key={i} className="text-sm text-[color:var(--tf-forest)] leading-snug" data-testid={`kb-governance-${i}`}>{g}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Acknowledge CTA */}
      <div className="tf-card rounded-xl p-5">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <p className="text-sm font-semibold text-[color:var(--tf-forest)]">
              {acked && !recert ? (
                <>
                  <CheckCircle2 className="w-4 h-4 inline text-emerald-500 mr-1" />
                  Acknowledged {new Date(data.progress.acked_at).toLocaleDateString()}
                </>
              ) : recert ? (
                'Content has been updated — please re-acknowledge'
              ) : (
                "Read the criteria above, then confirm you're familiar with them."
              )}
            </p>
            {acked && !recert && (
              <p className="text-xs text-[color:var(--tf-muted)] mt-0.5">
                We&apos;ll notify you if the criteria change.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowQuiz((s) => !s)}
              className="tf-btn-secondary px-3 py-2 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5"
              data-testid="kb-training-toggle-quiz-btn"
            >
              <HelpCircle className="w-3.5 h-3.5" />
              {showQuiz ? 'Hide quiz' : 'Take quiz instead'}
            </button>
            <button
              type="button"
              onClick={handleAck}
              disabled={ackBusy || (acked && !recert)}
              className={`tf-btn-gold px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed ${(acked && !recert) ? 'grayscale' : ''}`}
              data-testid="kb-training-ack-btn"
            >
              {ackBusy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : recert ? (
                <RotateCcw className="w-3.5 h-3.5" />
              ) : (
                <Award className="w-3.5 h-3.5" />
              )}
              {acked && !recert ? 'Acknowledged' : recert ? 'Re-acknowledge' : 'I understand'}
            </button>
          </div>
        </div>
        {showQuiz && (
          <div className="mt-4 pt-4 border-t border-[color:var(--tf-forest)]/10" data-testid="kb-training-quiz-section">
            <p className="tf-eyebrow mb-2">Prove it — pass 80% to auto-acknowledge</p>
            <InlineQuiz
              kbId={kbId}
              token={token}
              onPassed={() => {
                // Refetch detail so ack state updates
                axios
                  .get(`${API}/kb-training/kbs/${kbId}`, { headers: authHeaders(token) })
                  .then((r) => setData(r.data));
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default KbTrainingDetail;
